import { useId } from 'react'

import type { ChangeTone } from './format'

const WIDTH = 120
const HEIGHT = 32

/**
 * A trend glyph, not a chart: decorative, so hidden from assistive technology.
 * A `null` value is a point the source could not give: the line breaks there
 * rather than dropping to zero, and only an unbroken run is shaded. A point
 * with a gap on both sides is drawn as a dot, so it does not vanish.
 */
export function Sparkline({ values, tone = 'neutral' }: { values: readonly (number | null)[]; tone?: ChangeTone }) {
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const known = values.filter((value): value is number => value !== null)
  if (values.length < 2 || known.length < 2) return null

  const min = Math.min(...known)
  const max = Math.max(...known)
  const span = max - min || 1
  const step = WIDTH / (values.length - 1)
  const points = values.map((value, index) =>
    value === null ? null : ([index * step, HEIGHT - 2 - ((value - min) / span) * (HEIGHT - 4)] as const),
  )
  const line = points
    .map((point, index) => {
      if (!point) return ''
      const [x, y] = point
      return `${index === 0 || !points[index - 1] ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .filter(Boolean)
    .join(' ')
  const isolated = points.filter(
    (point, index): point is readonly [number, number] => point !== null && !points[index - 1] && !points[index + 1],
  )
  const complete = known.length === values.length
  const area = complete ? `${line} L${WIDTH},${HEIGHT} L0,${HEIGHT} Z` : null

  return (
    <svg
      className="ci-sparkline"
      data-tone={tone}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {area ? <path d={area} fill={`url(#${gradientId})`} /> : null}
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {isolated.map(([x, y]) => (
        <circle key={x} cx={x} cy={y} r="1.5" fill="currentColor" />
      ))}
    </svg>
  )
}
